import React, { useState, useContext } from 'react';
import _ from 'lodash';
// import { styled } from '@this/constants/themes';
import type { DateRangeFormatFunction, stringOrDate } from 'react-big-calendar';
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar';
import type { Moment } from 'moment';
import moment from 'moment';
import type { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import Color from 'color';
import Event, { convertCandidateToArgs } from '@this/domain/appoint/event';
// import Calendar from '@this/domain/appoint/calendar';
import type Candidate from '@this/domain/appoint/candidate';
import { CalendarWrapper, /* CalendarLoading, */ formats, messages } from '@this/components/appoint/common';
import EventPopupCard from '@this/components/appoint/shared/event_popup_card/event_popup_card';
import AppointContext from '@this/components/appoint/appoint_context';
import muiTheme from '@this/components/appoint/mui_theme';

const localizer = momentLocalizer(moment);

interface Props {
  // eventLists: Event[][];
  candidates: Candidate[];
  candidateSlots: Candidate[];
  // calendars: Calendar[];
  selectedCandidate: Candidate | null | undefined;
  date: Moment;
  // isEventLoading: boolean;
  onDateChange: (date: Moment) => void;
  setSelectedDate: (date: MaterialUiPickersDate) => void;
  setSelectedCandidate: (candidate: Candidate | null) => void;
}

type SlotInfo = {
  start: stringOrDate;
  end: stringOrDate;
  slots: Date[] | string[];
  action: 'select' | 'click' | 'doubleClick';
};

const ShowBigCalendar = ({
  candidates,
  candidateSlots,
  selectedCandidate,
  date,
  onDateChange,
  setSelectedDate,
  setSelectedCandidate
}: Props) => {
  const contextValue = useContext(AppointContext);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [eventClickX, setEventClickX] = useState<number>(0);
  const [eventClickY, setEventClickY] = useState<number>(0);
  const [calendarScrollTo, setCalendarScrollTo] = useState<Date | undefined>(
    moment().set({ hour: 5, minute: 50 }).toDate()
  );
  const [selectedCalendarView, setSelectedCalendarView] = useState<'week' | 'day'>('week');
  const [touchStartedX, setTouchStartedX] = useState<number>(0);
  const [touchMovedX, setTouchMovedX] = useState<number>(0);
  const [isLongTapInfoShown, setIsLongTapInfoShown] = useState<boolean>(false);

  if (contextValue.isMobile) {
    const dayRangeHeaderFormat: DateRangeFormatFunction = ({ start }) => {
      const s = moment(start);
      return s.format('YYYY年MM月DD日(ddd) 週');
    };
    formats.dayRangeHeaderFormat = dayRangeHeaderFormat;
  }

  let events: Event[] = [];
  // if (eventLists.length > 0) {
  //   events = eventLists.flat();
  // }
  events = [
    ...events,
    ...candidates
      .map(c => {
        return new Event(convertCandidateToArgs(c, '候補日'));
      })
      .flat()
  ];
  if (selectedCandidate) {
    events.push(new Event({ name: '選択中', start: selectedCandidate.start, end: selectedCandidate.end }));
  }

  const getEventProp = (e: Event) => {
    // const calendar = _.find(calendars, c => c.id === e.calendarId);
    const eventProps = {
      className: e.end.valueOf() - e.start.valueOf() < 1000 * 60 * 45 ? 'short_event' : '',
      style: {}
    };
    // let backgroundColor = new Color(
    //   calendar && calendar.backgroundColor ? calendar.backgroundColor : muiTheme.palette.primary.main
    // );
    // if (e.end < moment()) {
    //   if (calendar && calendar.backgroundColor) {
    //     const lightness = backgroundColor.lightness();
    //     const newLightness = 100 - (100 - lightness) / 5;
    //     backgroundColor = backgroundColor.lightness(newLightness);
    //   } else {
    //     backgroundColor = backgroundColor.lighten(0.5);
    //   }
    // }
    // if (calendar && calendar.backgroundColor) {
    //   const color = new Color('#ffffff');
    //   eventProps.style = {
    //     backgroundColor: backgroundColor.hex(),
    //     color: calendar.foregroundColor || '#1d1d1d',
    //     borderColor: color.darken(0.0).hex()
    //   };
    //   return eventProps;
    // }
    // 選択中はメインカラー
    if (e.name === '選択中') {
      eventProps.style = {
        backgroundColor: muiTheme.palette.primary.main,
        color: '#fff',
        borderColor: new Color(muiTheme.palette.primary.main).darken(0.3).hex()
      };
      return eventProps;
    }
    // 候補日は破線ボーダー
    eventProps.className += ' adjustment-candidate-event';
    eventProps.style = {
      backgroundColor: '#fff',
      color: muiTheme.palette.primary.main,
      border: `dashed 2px ${muiTheme.palette.primary.main}`
    };
    return eventProps;
  };

  const handleSelectEvent = (event: Event, e: React.SyntheticEvent<HTMLElement, globalThis.Event>) => {
    if (selectedEvent?.id === event.id) {
      setSelectedEvent(null);
      return;
    }
    e.stopPropagation();
    setSelectedEvent(event);
    const click = e.nativeEvent as MouseEvent;
    setEventClickX(click.pageX - 400);
    setEventClickY(click.pageY - 50);
  };

  const handleSelectSlot = (slot: SlotInfo) => {
    const slotStart = moment(slot.start);
    setSelectedDate(slotStart);
    setSelectedEvent(null);
    let selected = _.find(candidateSlots, cs => cs.start.format() === slotStart.format());
    // 15分刻みでスロットを選択可能であるため、上で候補枠が見つからない場合は15分遡って0分か30分開始のものも探す
    if (selected === undefined) {
      selected = _.find(
        candidateSlots,
        cs => cs.start.format() === slotStart.clone().subtract(15, 'minutes').format()
      );
    }
    if (selected) {
      contextValue.setOpenSnackbar(false);
      setIsLongTapInfoShown(true);
      // 選択済みのスロットと一致する場合は選択解除
      setSelectedCandidate(selectedCandidate?.start.format() === selected.start.format() ? null : selected);
    }
  };

  const handleRangeChange = (val: any) => {
    setSelectedCalendarView(val.length === 1 ? 'day' : 'week');
    setCalendarScrollTo(moment().set({ hour: 5, minute: 50 }).toDate());
  };

  const handleTouchStart = (e: any) => {
    setTouchStartedX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: any) => {
    setTouchMovedX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStartedX !== 0 && touchMovedX !== 0) {
      const diff = selectedCalendarView === 'day' ? 1 : 7;
      if (touchStartedX - touchMovedX > 150) {
        onDateChange(date.add(diff, 'days'));
      }

      if (touchStartedX - touchMovedX < -150) {
        onDateChange(date.subtract(diff, 'days'));
      }
    }
    setTouchStartedX(0);
    setTouchMovedX(0);
  };

  const handleCalendarMarginTap = () => {
    if (!contextValue.isMobile || isLongTapInfoShown) {
      return;
    }

    contextValue.setSnackbarContent({
      message: '候補日スロット内をロングタップで候補日時を選択',
      severity: 'info'
    });
    contextValue.setOpenSnackbar(true);
    setIsLongTapInfoShown(true);
  };

  return (
    <>
      <EventPopupCard
        event={selectedEvent}
        x={eventClickX}
        y={eventClickY}
        onClose={() => setSelectedEvent(null)}
      />
      <CalendarWrapper
        isMobile={contextValue.isMobile}
        onClick={() => handleCalendarMarginTap()}
        onTouchStart={e => handleTouchStart(e)}
        onTouchMove={e => handleTouchMove(e)}
        onTouchEnd={() => handleTouchEnd()}
      >
        {/* {isEventLoading && <TheCalendarLoading />} */}
        <BigCalendar
          localizer={localizer}
          events={events}
          titleAccessor="name"
          startAccessor={event => event.start.toDate()}
          endAccessor={event => event.end.toDate()}
          step={15} // 前後の確保時間の関係で15分ズレたスロットが発生した場合にそのスロットを選択可能にするため
          timeslots={4} // 同上
          selectable
          longPressThreshold={300}
          onRangeChange={val => handleRangeChange(val)}
          onSelectEvent={(event, e) => handleSelectEvent(event, e)}
          onSelectSlot={slot => handleSelectSlot(slot)}
          defaultView="week"
          views={['day', 'week']}
          date={date.toDate()}
          eventPropGetter={e => getEventProp(e)}
          onNavigate={date => {
            onDateChange(moment(date));
          }}
          formats={formats}
          scrollToTime={calendarScrollTo}
          messages={messages}
        />
      </CalendarWrapper>
    </>
  );
};

// const TheCalendarLoading = styled(CalendarLoading)`
//   top: 3px;
//   left: 200px;
// `;

export default ShowBigCalendar;
